import React from 'react';
import { Chip, Stack, Tooltip } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';

// Compact one-line representation of a booth assignment, mirroring the chip
// shown on the vendor details page so every table view (dashboard, approvals,
// final registrations, etc.) stays visually consistent.
//
// Pass the booth's number/name plus the two flags and the chip will:
//   - show "<name>" outlined for a regular booth,
//   - append " • Corner" and switch to the filled variant when it's a corner,
//   - prepend a bolt icon when the booth needs electricity.
//
// `size` defaults to "small" since this is almost always rendered inside a
// dense MUI table row.
const BoothChip = ({
  name,
  isCorner = false,
  needsElectricity = false,
  size = 'small',
  variant,
  color = 'primary',
  sx,
  ...rest
}) => {
  if (!name) return null;
  const tooltipParts = [];
  if (isCorner) tooltipParts.push('Corner booth');
  if (needsElectricity) tooltipParts.push('Needs electricity');
  const label = `${name}${isCorner ? ' • Corner' : ''}`;
  const chip = (
    <Chip
      size={size}
      color={color}
      variant={variant || (isCorner ? 'filled' : 'outlined')}
      icon={needsElectricity ? <BoltIcon /> : undefined}
      label={label}
      sx={sx}
      {...rest}
    />
  );
  if (tooltipParts.length === 0) return chip;
  return <Tooltip title={tooltipParts.join(' • ')}>{chip}</Tooltip>;
};

// Render a list of booths inline. Accepts an array of objects shaped like
// { name|booth_name|cell_name, is_corner, needs_electricity } so callers can
// hand us whatever shape the API/server already gives them.
export const BoothChipList = ({ booths = [], emptyLabel = 'No booth', spacing = 0.5, sx }) => {
  const normalized = booths
    .map((b) => ({
      name: b?.name || b?.booth_name || b?.cell_name,
      isCorner: Boolean(b?.is_corner ?? b?.isCorner),
      needsElectricity: Boolean(b?.needs_electricity ?? b?.needsElectricity),
    }))
    .filter((b) => b.name);

  if (normalized.length === 0) {
    if (!emptyLabel) return null;
    return <Chip size="small" label={emptyLabel} variant="outlined" sx={sx} />;
  }

  return (
    <Stack direction="row" spacing={spacing} sx={{ flexWrap: 'wrap', gap: spacing, ...sx }}>
      {normalized.map((b, index) => (
        <BoothChip
          key={`${b.name}-${index}`}
          name={b.name}
          isCorner={b.isCorner}
          needsElectricity={b.needsElectricity}
        />
      ))}
    </Stack>
  );
};

export default BoothChip;
